import {EventService} from '@/services/EventService.js';
import {v4 as uuidV4} from 'uuid';

// Events that should be handled by the central Event Service.
const CENTRALIZED_EVENTS = ['click'];
const isCentralized = (eventType) => CENTRALIZED_EVENTS.includes(eventType);

const wrapForClickability = (graphic) => (handler) => (...args) => {
  if (!graphic.isClickable()) return;
  return handler(...args);
};

class EventHandling {
  constructor() {
    /** @type {string} Standard UUID with dashes replaced by underscore for compatibility with SVG sub-elements */
    this.uuid = uuidV4().replaceAll('-', '_');
    /** @type {Record<string, Function>} */
    this._handlerWrappers = {
      'click': wrapForClickability(this),
    };
  }

  /**
   * Adds a new event handler to this object.
   * @param {string} eventType The type of event for which to invoke the handler.
   * @param {string} handlerName A name identifying this handler for later removal.
   * @param {Function} handler The handler to be invoked.
   */
  addHandler(eventType, handlerName, handler) {
    let wrapped = handler;
    if (this._handlerWrappers && (eventType in this._handlerWrappers)) {
      wrapped = this._handlerWrappers[eventType](wrapped);
    }
    if (isCentralized(eventType)) {
      addCentralizedHandler(this.uuid, handlerName, wrapped);
    } else {
      this.addLocalHandler(eventType, handlerName, wrapped);
    }
  }

  /**
   * Adds a new local event handler for this object.
   * Objects handling events not listed in `CENTRALIZED_EVENTS` must override this method.
   * @protected
   * @param {string} eventType The type of event the handler covers.
   * @param {string} handlerName A name identifying this handler for later removal.
   * @param {Function} handler The handler to be invoked.
   */
  addLocalHandler(eventType, handlerName, handler) {
    console.warn(`EventHandling.addLocalHandler was called and is not overridden. Subclasses of EventHandling handling non-centralized events must override addLocalHandler.`);
  }

  /**
   * Removes a previously-registered event handler from this object.
   * @param {string} eventType The type of event the handler is invoked for.
   * @param {string} handlerName The name identifying the handler to remove.
   * @param {Function} handler The handler to remove. Ideally this should not be required, but
   * some implementations of removeLocalHandler need it.
   * @return {boolean | undefined} `true` if the handler existed and was removed; `false` if not.
   * May return `undefined` if the implementation of removeLocalHandler doesn't return a value.
   */
  removeHandler(eventType, handlerName, handler) {
    if (isCentralized(eventType)) {
      return removeCentralizedHandler(this.uuid, handlerName);
    } else {
      return this.removeLocalHandler(eventType, handlerName, handler);
    }
  }

  /**
   * Removes a local event handler from this object.
   * Objects handling events not listed in `CENTRALIZED_EVENTS` must override this method.
   * @protected
   * @param {string} eventType The type of event the handler is invoked for.
   * @param {string} handlerName The name identifying the handler to remove.
   * @param {Function} handler The handler to remove. Ideally this should not be required, but
   * some implementations need it.
   */
  removeLocalHandler(eventType, handlerName, handler) {
    console.warn(`EventHandling.removeLocalHandler was called and is not overridden. Subclasses of EventHandling handling non-centralized events must override removeLocalHandler.`);
  }
}

// Private method implementations for Event Handling
/**
 * Adds a new centralized event handler.
 * @private
 * @param {string} uuid The unique ID of the object adding the handler.
 * @param {string} handlerName A name identifying this handler for later removal.
 * @param {Function} handler The handler to be invoked.
 */
function addCentralizedHandler(uuid, handlerName, handler) {
  EventService.addHandler(uuid, handlerName, handler);
}
/**
 * Removes a centralized event handler from this object.
 * @private
 * @param {string} uuid The unique ID of the object removing the handler.
 * @param {string} handlerName The name identifying the handler to remove.
 * @return {boolean} `true` if the handler existed and was removed; otherwise `false`.
 */
function removeCentralizedHandler(uuid, handlerName) {
  return EventService.removeHandler(uuid, handlerName);
}

const MAPS_ADDLISTENER_MIXIN_HANDLERS = Symbol('mapsAddListenerMixin: named MapsEventListeners');
/**
 * Defines a mixin for Graphic classes that use `MVCObject.addListener` /  `MapsEventListener`.
 * To be able to remove a `MapsEventListener`, a reference to it must be retained by name.
 * This mixin takes care of keeping track of the names and calling the `remove` method when needed.
 * @param {string} mapsObjectProperty The name of the property on the Graphic subclass holding the
 * Maps object on which `addListener` will be invoked.
 * @return {object} A mixin object to be added to the Graphic's prototype.
 */
const mapsAddListenerMixin = (mapsObjectProperty) => {
  return {
    /**
     * Adds a local event listener to this object's underlying Maps object.
     * @param {string} eventType The type of event the handler covers.
     * @param {string} handlerName A name identifying this handler for later removal.
     * @param {Function} handler The handler to be invoked.
     */
    addLocalHandler(eventType, handlerName, handler) {
      const listener = this[mapsObjectProperty]?.addListener(eventType, handler);
      if (!this[MAPS_ADDLISTENER_MIXIN_HANDLERS]) {
      /** @type {Map<string, google.maps.MapsEventListener>} */
        this[MAPS_ADDLISTENER_MIXIN_HANDLERS] = new Map();
      }
      this[MAPS_ADDLISTENER_MIXIN_HANDLERS].set(handlerName, listener);
    },
    /**
     * Removes a local event handler from this object's underlying Maps object.
     * @param {string} eventType The type of event the handler is invoked for.
     * @param {string} handlerName The name identifying the handler to remove.
     * @param {Function} handler The handler to remove.
     * @return {boolean} `true` if the handler existed and was removed; otherwise `false`.
     */
    removeLocalHandler(eventType, handlerName, handler) {
    /** @type {google.maps.MapsEventListener} */
      const mapsEventListener = this[MAPS_ADDLISTENER_MIXIN_HANDLERS]?.get(handlerName);
      if (!mapsEventListener) return false;
      mapsEventListener.remove();
      return this[MAPS_ADDLISTENER_MIXIN_HANDLERS]?.delete(handlerName);
    },
  };
};

export {
  EventHandling,
  mapsAddListenerMixin,
};
